import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import Cookies from 'js-cookie';

const getBearerToken = () => {
    return Cookies.get('auth_token');
};

const getPlatformStoreMirror = ({ platform, storeName, mirrorIds }, paramsObj) => {
    if (platform) {
        paramsObj.platform = platform
    }
    if (storeName?.length > 0) {
        paramsObj.storeName = storeName
    }
    if (mirrorIds?.length > 0) {
        paramsObj.mirrorIds = mirrorIds
    }
}

export const skinDashboardApi = createApi({
    baseQuery: fetchBaseQuery({
        baseUrl: `${process.env.REACT_APP_BASE_URL}`,
        prepareHeaders(headers) {
            const token = getBearerToken();
            headers.set('Authorization', `Bearer ${token}`);
            return headers;
        },
    }),

    endpoints: (builder) => ({
        // skin
        fetchAnalyticsData: builder.query({
            query: ({ type, storeNumbers, startDate, endDate, platform, storeName, mirrorIds, }) => {
                const params = {
                    storeNumbers,
                    startDate,
                    endDate,
                }
                getPlatformStoreMirror({ platform, storeName, mirrorIds }, params);
                return {
                    url: `analytics/${type}/Dashboard`,
                    params: params,
                }
            },
        }),
        getProfile: builder.query({
            query: () => 'admin/getProfile',
        }),
        getStore: builder.query({
            query: () => 'analytics/skin/getStore',
        }),
        getMirror: builder.query({
            query: (body) => ({
                url: 'analytics/skin/getMirror',
                method: 'POST',
                body,
            }),
        }),
        getUserFeedback: builder.query({
            query: ({ storeNumbers, type }) => `admin/userFeedback?storeNumbers=${storeNumbers}&feedbackOf=${type}`,
        }),
        getLiveSkus: builder.query({
            query: () => 'admin/getLiveSkus',
        }),
        fetchTotalUsers: builder.query({
            query: ({ type, storeNumbers, startDate, endDate, platform, storeName, mirrorIds, }) => {
                const params = {
                    storeNumbers,
                    startDate,
                    endDate,
                }
                getPlatformStoreMirror({ platform, storeName, mirrorIds }, params);
                return {
                    url: `analytics/${type}/totalUsers`,
                    params: params,
                }
            },
        }),
        fetchTotalSessions: builder.query({
            query: ({ type, storeNumbers, startDate, endDate, platform, storeName, mirrorIds, }) => {
                const params = {
                    storeNumbers,
                    startDate,
                    endDate,
                }
                getPlatformStoreMirror({ platform, storeName, mirrorIds }, params);
                return {
                    url: `analytics/${type}/totalSessions`,
                    params: params,
                }
            },
        }),
        fetchTotalUserDropCount: builder.query({
            query: ({ storeNumbers, startDate, endDate }) =>
                `analytics/skin/totalUserDropCount?storeNumbers=${storeNumbers}&startDate=${startDate}&endDate=${endDate}`,
        }),
        fetchTotalAvgMinutes: builder.query({
            query: ({ type, storeNumbers, startDate, endDate, platform, storeName, mirrorIds, }) => {
                const params = {
                    storeNumbers,
                    startDate,
                    endDate,
                }
                getPlatformStoreMirror({ platform, storeName, mirrorIds }, params);
                return {
                    url: `analytics/${type}/totalAvgMinutes`,
                    params: params,
                }
            },
        }),
        fetchTotalTopTen: builder.query({
            query: ({ type, storeNumbers, startDate, endDate, platform, storeName, mirrorIds, }) => {
                const params = {
                    storeNumbers,
                    startDate,
                    endDate,
                }
                getPlatformStoreMirror({ platform, storeName, mirrorIds }, params);
                return {
                    url: `analytics/skin/totalTopTen`,
                    params: params,
                }
            },
        }),
        fetchTotalSmsWhatsapp: builder.query({
            query: ({ type, storeNumbers, startDate, endDate, platform, storeName, mirrorIds, }) => {
                const params = {
                    storeNumbers,
                    startDate,
                    endDate,
                }
                getPlatformStoreMirror({ platform, storeName, mirrorIds }, params);
                return {
                    url: `analytics/${type}/totalSmsWhatsapp`,
                    params: params,
                }
            },
        }),
        fetchAddToCart: builder.query({
            query: ({ type, storeNumbers, startDate, endDate, platform, storeName, mirrorIds, }) => {
                const params = {
                    storeNumbers,
                    startDate,
                    endDate,
                }
                getPlatformStoreMirror({ platform, storeName, mirrorIds }, params);
                return {
                    url: `analytics/${type}/totalCart`,
                    params: params,
                }
            },
        }),
        fetchTotalRecommendation: builder.query({
            query: ({ type, storeNumbers, startDate, endDate, platform, storeName, mirrorIds, }) => {
                const params = {
                    storeNumbers,
                    startDate,
                    endDate,
                }
                getPlatformStoreMirror({ platform, storeName, mirrorIds }, params);
                return {
                    url: `analytics/${type}/totalRecommendation`,
                    params: params,
                }
            },
        }),
        fetchTotalSkinConcern: builder.query({
            query: ({ type, storeNumbers, startDate, endDate, platform, storeName, mirrorIds, }) => {
                const params = {
                    storeNumbers,
                    startDate,
                    endDate,
                }
                getPlatformStoreMirror({ platform, storeName, mirrorIds }, params);
                return {
                    url: `analytics/${type}/total${type.charAt(0).toUpperCase() + type.slice(1).toLowerCase()}Concern`,
                    params: params,
                }
            },
        }),
        fetchTotalAgeGroup: builder.query({
            query: ({ type, storeNumbers, startDate, endDate, platform, storeName, mirrorIds, }) => {
                const params = {
                    storeNumbers,
                    startDate,
                    endDate,
                }
                getPlatformStoreMirror({ platform, storeName, mirrorIds }, params);
                return {
                    url: `analytics/${type}/totalAgeGroup`,
                    params: params,
                }
            },
        }),
        fetchTotalSkinType: builder.query({
            query: ({ type, storeNumbers, startDate, endDate, platform, storeName, mirrorIds, }) => {
                const params = {
                    storeNumbers,
                    startDate,
                    endDate,
                }
                getPlatformStoreMirror({ platform, storeName, mirrorIds }, params);
                return {
                    url: `analytics/skin/totalSkinType`,
                    params: params,
                }
            },
        }),
        getMirrorStatus: builder.query({
            query: () => 'admin/getMirrosStatus',
        }),

        //makeup
        fetchMakeupTotalUsers: builder.query({
            query: ({ storeNumbers, startDate, endDate }) =>
                `analytics/makeup/totalUser?storeNumbers=${storeNumbers}&startDate=${startDate}&endDate=${endDate}`,
        }),
        fetchMakeupTotalWishlistLook: builder.query({
            query: ({ storeNumbers, startDate, endDate }) =>
                `analytics/makeup/totalWishlistLook?storeNumbers=${storeNumbers}&startDate=${startDate}&endDate=${endDate}`,
        }),
        fetchMakeupTop20: builder.query({
            query: ({ storeNumbers, startDate, endDate }) =>
                `analytics/makeup/top20?storeNumbers=${storeNumbers}&startDate=${startDate}&endDate=${endDate}`,
        }),
        fetchMakeupAvgPresentMakeup: builder.query({
            query: ({ storeNumbers, startDate, endDate }) =>
                `analytics/makeup/avgPresentMakeup?storeNumbers=${storeNumbers}&startDate=${startDate}&endDate=${endDate}`,
        }),
        fetchMakeupTotalUserDistribution: builder.query({
            query: ({ storeNumbers, startDate, endDate }) =>
                `analytics/makeup/totalUserDis?storeNumbers=${storeNumbers}&startDate=${startDate}&endDate=${endDate}`,
        }),
    }),
});

export const {
    useFetchAnalyticsDataQuery,
    useGetProfileQuery,
    useGetStoreQuery,
    useLazyGetStoreQuery,
    useGetMirrorQuery,
    useLazyGetMirrorQuery,
    useGetUserFeedbackQuery,
    useGetLiveSkusQuery,
    useGetMirrorStatusQuery,
    useFetchTotalUsersQuery,
    useFetchTotalSessionsQuery,
    useFetchTotalUserDropCountQuery,
    useFetchTotalAvgMinutesQuery,
    useFetchTotalTopTenQuery,
    useFetchTotalSmsWhatsappQuery,
    useFetchTotalRecommendationQuery,
    useFetchTotalSkinConcernQuery,
    useFetchTotalAgeGroupQuery,
    useFetchTotalSkinTypeQuery,
    useFetchAddToCartQuery,
    useFetchMakeupAvgPresentMakeupQuery,
    useFetchMakeupTop20Query,
    useFetchMakeupTotalUserDistributionQuery,
    useFetchMakeupTotalUsersQuery,
    useFetchMakeupTotalWishlistLookQuery,
} = skinDashboardApi;